/**
 * Plan Scorer — Pure scoring engine for plan recommendation
 * Operates on plan objects + questionnaire answers. No DOM, no API calls.
 */

const BUDGET_RANGES = {
  under_200: [0, 200],
  '200_400': [200, 400],
  '400_700': [400, 700],
  '700_plus': [700, Infinity],
  unsure: [0, Infinity],
};

const MEDICAL_CATEGORIES = [
  'Short-Term Medical',
  'ACA Health',
  'Health',
  'Major Medical',
  'Hospital Indemnity',
  'Fixed Indemnity',
];

const ANCILLARY_CATEGORIES = [
  'Dental',
  'Vision',
  'Accident',
  'Critical Illness',
  'Life',
];

const UNDERWRITTEN_ISSUE_TYPES = [
  'Simplified Issue',
  'Full Underwriting',
  'Medical Underwriting',
];

// Categories that indicate comprehensive coverage (good for Rx-heavy users)
const COMPREHENSIVE_CATEGORIES = ['ACA Health', 'Major Medical'];

// --- Helpers ---

function getDeductible(plan) {
  return plan.deductible ?? plan.planDetails?.deductible ?? 0;
}

function getIssueType(plan) {
  return plan.planDetails?.issueType || '';
}

function isMedicalCategory(category) {
  return MEDICAL_CATEGORIES.some(
    mc => category.toLowerCase().includes(mc.toLowerCase()),
  );
}

function isAncillaryCategory(category) {
  return ANCILLARY_CATEGORIES.some(
    ac => category.toLowerCase().includes(ac.toLowerCase()),
  );
}

function isUnderwritten(issueType) {
  return UNDERWRITTEN_ISSUE_TYPES.some(
    ut => ut.toLowerCase() === issueType.toLowerCase(),
  );
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// --- Eligibility ---

/**
 * Returns a filter function that checks plan eligibility based on answers.
 * @param {Object} answers - Questionnaire answers
 * @returns {function(Object): boolean} Filter function
 */
export function getEligibilityFilter(answers) {
  const hasSignificantConditions = answers.conditions === 'significant';
  const hasPlannedSurgery = answers.plannedSurgery === 'yes';

  return function isEligible(plan) {
    const issueType = getIssueType(plan);
    if (!issueType) return true;

    // Significant conditions or planned surgery → exclude underwritten plans
    if ((hasSignificantConditions || hasPlannedSurgery) && isUnderwritten(issueType)) {
      return false;
    }

    return true;
  };
}

// --- Scoring ---

/**
 * Score a single plan against questionnaire answers.
 * @param {Object} plan - Plan object with id, name, premium, category, planDetails
 * @param {Object} answers - Questionnaire answers
 * @returns {number} Score 0-100
 */
export function scorePlan(plan, answers) {
  // Check eligibility first — ineligible plans score 0
  const eligibilityFilter = getEligibilityFilter(answers);
  if (!eligibilityFilter(plan)) return 0;

  // Component scores (each 0-100)
  const budgetScore = scoreBudget(plan, answers);
  const healthScore = scoreHealth(plan, answers);
  const usageScore = scoreUsage(plan, answers);
  const rxScore = scoreRx(plan, answers);
  const networkScore = 70; // Baseline — no actual network data yet

  // Base weights
  let budgetWeight = 0.30;
  let healthWeight = 0.25;
  let usageWeight = 0.20;
  let rxWeight = 0.10;
  let networkWeight = 0.10;
  // Priority adjustment draws from the 5% reserve
  const priorityReserve = 0.05;

  // Priority adjustments — spec: lowest_price +10pp to budget, best_coverage +5pp each to health/rx,
  // keep_doctors +10pp to network. Always redistribute from other weights to keep sum = 1.0.
  if (answers.priority === 'lowest_price') {
    budgetWeight += 0.10;       // 30% → 40%
    healthWeight -= 0.04;
    usageWeight -= 0.03;
    rxWeight -= 0.015;
    networkWeight -= 0.015;
  } else if (answers.priority === 'best_coverage') {
    healthWeight += 0.05;       // 25% → 30%
    rxWeight += 0.05;           // 10% → 15%
    budgetWeight -= 0.05;
    usageWeight -= 0.03;
    networkWeight -= 0.02;
  } else if (answers.priority === 'keep_doctors') {
    networkWeight += 0.10;      // 10% → 20%
    budgetWeight -= 0.04;
    healthWeight -= 0.03;
    usageWeight -= 0.02;
    rxWeight -= 0.01;
  } else {
    // No priority or unknown — distribute 5% reserve proportionally
    budgetWeight += priorityReserve * 0.30;
    healthWeight += priorityReserve * 0.25;
    usageWeight += priorityReserve * 0.20;
    rxWeight += priorityReserve * 0.15;
    networkWeight += priorityReserve * 0.10;
  }

  const raw = (budgetScore * budgetWeight) +
    (healthScore * healthWeight) +
    (usageScore * usageWeight) +
    (rxScore * rxWeight) +
    (networkScore * networkWeight);

  return Math.round(clamp(raw, 0, 100));
}

function scoreBudget(plan, answers) {
  const range = BUDGET_RANGES[answers.budget] || BUDGET_RANGES.unsure;
  const [min, max] = range;
  const premium = plan.premium || 0;

  if (max === Infinity && min === 0) {
    // unsure — all premiums are acceptable, slight preference for lower
    return clamp(100 - (premium / 20), 50, 100);
  }

  if (premium >= min && premium <= max) {
    // Within range — full score, with slight preference for lower end
    const position = max === min ? 0 : (premium - min) / (max - min);
    return Math.round(100 - (position * 15)); // 85-100
  }

  if (premium < min) {
    // Under budget
    return 80;
  }

  // Over budget — score drops proportionally
  const overBy = premium - max;
  const dropRate = overBy / max; // Fraction over budget
  return Math.round(clamp(80 - (dropRate * 100), 0, 80));
}

function scoreHealth(plan, answers) {
  const issueType = getIssueType(plan);
  const hasConditions = answers.conditions === 'significant';

  if (hasConditions) {
    // Significant conditions → guaranteed-issue and ACA score well
    if (issueType === 'Guaranteed Issue' || issueType === 'ACA Marketplace') {
      return 95;
    }
    // Non-underwritten but not explicitly guaranteed
    return 60;
  }

  // No significant conditions
  if (answers.conditions === 'minor') {
    // Minor conditions — slight preference for guaranteed-issue
    if (issueType === 'Guaranteed Issue' || issueType === 'ACA Marketplace') {
      return 85;
    }
    return 70;
  }

  // No conditions — STM scores slightly higher (cheaper, less restrictions)
  const category = plan.category || '';
  if (category.toLowerCase().includes('short-term')) {
    return 80;
  }
  return 70;
}

function scoreUsage(plan, answers) {
  const deductible = getDeductible(plan);
  const premium = plan.premium || 0;

  if (answers.doctorVisits === 'frequently') {
    // Frequent visits → prefer low deductible
    if (deductible <= 1000) return 95;
    if (deductible <= 3000) return 70;
    if (deductible <= 5000) return 50;
    return 30;
  }

  if (answers.doctorVisits === 'occasionally') {
    // Moderate usage — balanced preference
    if (deductible <= 2500) return 85;
    if (deductible <= 5000) return 70;
    return 55;
  }

  // Rarely — prefer low premium (accept high deductible)
  // Lower premium = higher score
  if (premium <= 150) return 95;
  if (premium <= 300) return 80;
  if (premium <= 500) return 65;
  return 50;
}

function scoreRx(plan, answers) {
  const category = plan.category || '';
  const isComprehensive = COMPREHENSIVE_CATEGORIES.some(
    cc => category.toLowerCase().includes(cc.toLowerCase()),
  );

  if (answers.prescriptions === 'three_plus' ||
      (answers.medications && answers.medications.length >= 3)) {
    // Heavy Rx needs → favor ACA/comprehensive
    return isComprehensive ? 95 : 50;
  }

  if (answers.prescriptions === 'one_two' ||
      (answers.medications && answers.medications.length >= 1 && answers.medications.length <= 2)) {
    return isComprehensive ? 80 : 65;
  }

  // None — all plans acceptable
  return 70;
}

// --- Bundle Generation ---

/**
 * Generate up to 3 recommended bundles from plans and answers.
 * @param {Object[]} plans - All available plans
 * @param {Object} answers - Questionnaire answers
 * @returns {Object[]} Array of bundle objects
 */
export function generateBundles(plans, answers) {
  if (!plans || plans.length === 0) return [];

  const eligibilityFilter = getEligibilityFilter(answers);

  // Split into medical vs ancillary
  const medicalPlans = plans.filter(p => isMedicalCategory(p.category || ''));
  const ancillaryPlans = plans.filter(p => isAncillaryCategory(p.category || ''));

  // Score and filter medical plans
  const scoredMedical = medicalPlans
    .filter(eligibilityFilter)
    .map(plan => ({ plan, score: scorePlan(plan, answers) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score);

  if (scoredMedical.length === 0) return [];

  // Determine requested ancillary types
  const requestedAncillary = getRequestedAncillaryTypes(answers);
  const budgetAncillary = getBudgetAncillaryTypes(answers);

  // Pick ancillary plans — cheapest per category
  const ancillaryByCategory = pickAncillaryByCategory(ancillaryPlans, requestedAncillary);
  const budgetAncillaryPicked = pickAncillaryByCategory(ancillaryPlans, budgetAncillary);

  const bundles = [];

  // 1. Budget bundle — cheapest eligible medical + dental only
  const cheapestMedical = [...scoredMedical].sort((a, b) => a.plan.premium - b.plan.premium)[0];
  bundles.push(buildBundle('budget', 'Budget-Friendly', cheapestMedical, budgetAncillaryPicked));

  // 2. Coverage bundle — highest-scoring medical + all requested ancillary
  const bestScoring = scoredMedical[0]; // Already sorted by score desc
  bundles.push(buildBundle('coverage', 'Best Coverage', bestScoring, ancillaryByCategory));

  // 3. Balanced bundle — mid-range medical + dental/vision only (only if 3+ medical plans)
  if (scoredMedical.length >= 3) {
    const midIndex = Math.floor(scoredMedical.length / 2);
    const midPlan = scoredMedical[midIndex];
    const balancedAncillary = pickAncillaryByCategory(
      ancillaryPlans,
      getBalancedAncillaryTypes(answers),
    );
    bundles.push(buildBundle('balanced', 'Balanced', midPlan, balancedAncillary));
  }

  return bundles;
}

function buildBundle(type, label, scoredMedical, ancillaryPicks) {
  const ancillary = Object.values(ancillaryPicks);
  const totalPremium = scoredMedical.plan.premium +
    ancillary.reduce((sum, p) => sum + p.premium, 0);

  return {
    type,
    label,
    medical: scoredMedical.plan,
    ancillary,
    score: scoredMedical.score,
    totalPremium,
  };
}

function getRequestedAncillaryTypes(answers) {
  const types = [];
  if (answers.dentalVision === 'dental' || answers.dentalVision === 'both') {
    types.push('Dental');
  }
  if (answers.dentalVision === 'vision' || answers.dentalVision === 'both') {
    types.push('Vision');
  }
  if (answers.accidentCritical === 'accident' || answers.accidentCritical === 'both') {
    types.push('Accident');
  }
  if (answers.accidentCritical === 'critical' || answers.accidentCritical === 'both') {
    types.push('Critical Illness');
  }
  return types;
}

function getBudgetAncillaryTypes(answers) {
  // Budget bundle: dental only (if requested)
  const types = [];
  if (answers.dentalVision === 'dental' || answers.dentalVision === 'both') {
    types.push('Dental');
  }
  return types;
}

function getBalancedAncillaryTypes(answers) {
  // Balanced bundle: dental + vision only (if requested)
  const types = [];
  if (answers.dentalVision === 'dental' || answers.dentalVision === 'both') {
    types.push('Dental');
  }
  if (answers.dentalVision === 'vision' || answers.dentalVision === 'both') {
    types.push('Vision');
  }
  return types;
}

function pickAncillaryByCategory(ancillaryPlans, requestedTypes) {
  const picks = {};
  for (const type of requestedTypes) {
    const matching = ancillaryPlans
      .filter(p => (p.category || '').toLowerCase().includes(type.toLowerCase()))
      .sort((a, b) => (a.premium || 0) - (b.premium || 0));
    if (matching.length > 0) {
      picks[type] = matching[0]; // Cheapest
    }
  }
  return picks;
}

// --- Namespace export ---

export const PlanScorer = {
  scorePlan,
  generateBundles,
  getEligibilityFilter,
};
